import { RULES_VERSION, rulesFor } from '@words-happen/board-rules-engine-client';
import type { Point } from '../geometry/polygon';
import { clockwise, insetPolygon, polygonPath, round2, roundedPolygonPath, unitTowards } from '../geometry/polygon';
import type {
  BonusCell,
  BonusKind,
  CellCoord,
  CellData,
  ClusterLink,
  Direction,
  TileCluster,
  TileData,
  ValidationResult,
} from './board-types';

const rules = rulesFor(RULES_VERSION);

/** The default layout: the rules' classic word premiums and the start square at the centre. */
const DEFAULT_BONUSES: BonusCell[] = [...rules.CLASSIC_PREMIUMS, { ...rules.centreCell(), kind: '★' }];

/** The board's cells, row by row, with the shape's holes and the bonuses — the default layout's unless given. */
export function getBoardLayout(
  shape: 'classic' | 'riverstone' | 'starfall',
  size = 11,
  bonuses?: BonusCell[],
): CellData[] {
  const bonusMap = new Map<string, BonusKind>();
  for (const b of bonuses ?? DEFAULT_BONUSES) bonusMap.set(`${b.row},${b.col}`, b.kind);
  return rules
    .boardCells(shape, size)
    .map((cell) => ({ ...cell, bonus: bonusMap.get(`${cell.row},${cell.col}`) ?? null }));
}

/**
 * Whether `dr` and `ur` runs are read as words, unless a move says otherwise. Diagonal play is an
 * experiment — see `docs/game-rules.md` — and this is the switch: with it `false` runs along the two
 * diagonals spell nothing, so a move that meets the board edge-on only at a corner does not connect,
 * and a cluster tile may not be turned onto a diagonal at all. `MoveInput.diagonals` overrides it per
 * move, which is how the simulator lab plays the same scenario both ways.
 *
 * Off by default: the rule costs about a third of a game (`docs/game-rules.md` section 5), so the game
 * is played without it until that price is justified.
 */
export const DIAGONAL_WORDS = false;

/**
 * Whether the tiles a player has laid **this turn** are drawn as one merged shape when they touch,
 * unless a board says otherwise. `BoardProps.mergeOwnTiles` overrides it.
 *
 * A tile in hand never merges with a committed one whatever this says — see {@link tileClusters}.
 * Whether it should merge with the player's other tiles in hand is a matter of taste rather than of
 * rules, and this is the switch to change our minds with.
 */
export const MERGE_OWN_TILES = true;

export const directionStep = (dir: Direction): { dr: number; dc: number } => rules.directionStep(dir);

export const isDiagonal = (dir: Direction): boolean => rules.isDiagonal(dir);

/**
 * The cells a tile covers, one per character, stepping from its anchor cell along its direction. Every direction advances
 * the column, so the anchor is always the tile's leftmost cell; `ur` is the one direction that climbs.
 */
export const computeCellsCovered = (tile: TileData): CellCoord[] => rules.cellsCovered(tile);

export interface BoardFit {
  /** Total pixel width the board is to occupy, chrome included. */
  boardWidth: number;
  size: number;
  gap: number;
  padding: number;
}

/**
 * The cell size a `size × size` board draws at to fill `boardWidth` — what `Board` derives from its
 * own `boardWidth` prop, exported because a consumer sizing a board to its layout needs the same
 * answer: the drop maths for a tile dragged in from outside runs on that cell size too (see
 * `cellFromDragEvent`), and a board and a drop that disagree on it put the tile down a cell out.
 */
export function fitCellSize({ boardWidth, size, gap, padding }: BoardFit): number {
  return Math.floor((boardWidth - 2 * padding - (size - 1) * gap) / size);
}

/**
 * Pixel footprint of a tile — the bounding box of the cells it covers, so a diagonal cluster tile
 * measures a square `len` cells on a side rather than the single-cell strip its letters occupy.
 */
export function tileFootprint(tile: TileData, cellSize: number, gap: number): { width: number; height: number } {
  const len = Math.max(tile.letter.length, 1);
  const span = len * cellSize + (len - 1) * gap;
  if (tile.dir === 'h') return { width: span, height: cellSize };
  if (tile.dir === 'v') return { width: cellSize, height: span };
  return { width: span, height: span };
}

/**
 * Top-left pixel offset of a tile's bounding box, relative to the board box — not of its anchor cell,
 * which for a `ur` run sits at the bottom-left of the box it occupies.
 */
export function tileOrigin(
  tile: TileData,
  cellSize: number,
  gap: number,
  padding: number,
): { left: number; top: number } {
  const len = Math.max(tile.letter.length, 1);
  const topRow = tile.dir === 'ur' ? tile.row - (len - 1) : tile.row;
  return {
    left: padding + tile.col * (cellSize + gap),
    top: padding + topRow * (cellSize + gap),
  };
}

/**
 * The tile's anchor pulled back onto the board, so every cell it covers lands on the grid. Only the
 * anchor moves — the tile keeps its direction and length — which is what a rotation needs: turning a
 * three-letter tile onto `ur` lifts its far end two rows, and without this the run leaves the board.
 */
export function anchorWithinBoard(tile: TileData, size: number): CellCoord {
  const covered = computeCellsCovered(tile);
  const shift = (values: number[]): number => {
    const min = Math.min(...values);
    const max = Math.max(...values);
    if (min < 0) return -min;
    if (max > size - 1) return size - 1 - max;
    return 0;
  };
  return {
    row: tile.row + shift(covered.map((c) => c.row)),
    col: tile.col + shift(covered.map((c) => c.col)),
  };
}

/**
 * Whether a tile fits where it lies: the rules' `tileFit` (on the grid, off holes, on no other tile, no cluster tile on
 * a diagonal with diagonals off), then `externalValidate` when given. A cell is a hole when `cells` says so.
 */
export function validateTile(
  tile: TileData,
  cells: CellData[],
  boardTiles: TileData[],
  size = 11,
  externalValidate?: (t: TileData, board: TileData[]) => ValidationResult,
  diagonals = DIAGONAL_WORDS,
): ValidationResult {
  // The holes come from `cells`, which may be any layout, so they are checked here, between the rules' own checks
  // in the order the rules make them.
  const unplaced = rules.tileFit(tile, 'classic', [], diagonals, size);
  if (unplaced) return { valid: false, reason: unplaced };
  const holes = new Set(cells.filter((c) => c.hole).map((c) => `${c.row},${c.col}`));
  if (computeCellsCovered(tile).some((c) => holes.has(`${c.row},${c.col}`))) {
    return { valid: false, reason: 'overlaps-hole' };
  }
  const collision = rules.tileFit(tile, 'classic', boardTiles, diagonals, size);
  if (collision) return { valid: false, reason: collision };
  return externalValidate ? externalValidate(tile, boardTiles) : { valid: true };
}

export function getBonusesHit(covered: CellCoord[], cells: CellData[]): BonusKind[] {
  const cellMap = new Map<string, CellData>();
  for (const c of cells) cellMap.set(`${c.row},${c.col}`, c);
  const bonuses: BonusKind[] = [];
  for (const coord of covered) {
    const cell = cellMap.get(`${coord.row},${coord.col}`);
    if (cell?.bonus) bonuses.push(cell.bonus);
  }
  return bonuses;
}

const cellKey = (row: number, col: number): string => `${row},${col}`;

/**
 * Neighbour offsets that link two occupied cells: right, down and both diagonals. Cells meeting at a
 * corner are linked whichever way either tile was laid, matching the move rules, which read runs
 * along all four axes. Checking forward offsets only visits each pair once.
 */
const LINK_STEPS: { dr: number; dc: number }[] = [
  { dr: 0, dc: 1 },
  { dr: 1, dc: 0 },
  { dr: 1, dc: 1 },
  { dr: -1, dc: 1 },
];

const isDiagonalStep = (step: { dr: number; dc: number }): boolean => step.dr !== 0 && step.dc !== 0;

/** A tile whose own cells step diagonally, so its footprint is a staircase rather than a rectangle. */
const isStaircase = (tile: TileData): boolean => tile.letter.length > 1 && isDiagonal(tile.dir);

/** A tile that has been played in an earlier turn, as opposed to one the player is still placing. */
const isCommitted = (tile: TileData): boolean => tile.placed === true;

/**
 * Whether two tiles may be drawn as one shape. A tile's own cells always may, however it lies; past
 * that, a tile in hand merges only with another in hand, and only while `mergeOwnTiles` allows it.
 */
const mergeable = (a: TileData, b: TileData, mergeOwnTiles: boolean): boolean => {
  if (a.id === b.id) return true;
  // `placed` is optional, so a tile in hand reads as `false` or as absent alike.
  const committed = isCommitted(a);
  if (committed !== isCommitted(b)) return false;
  return committed || mergeOwnTiles;
};

export type ClusterOptions = {
  /** Whether corner-touching tiles are joined. Defaults to {@link DIAGONAL_WORDS}. */
  diagonals?: boolean;
  /** Whether this turn's tiles merge with one another. Defaults to {@link MERGE_OWN_TILES}. */
  mergeOwnTiles?: boolean;
};

/**
 * Groups tiles into clusters of two or more that touch — side by side, or corner to corner. Each
 * cluster is drawn as one continuous shape (see `tileClusterGeometry`) rather than as separate
 * tiles; a tile touching no other keeps its own chrome.
 *
 * **A tile in hand never merges with a committed one.** A merged shape reads as one thing settled on
 * the board, which is exactly what a tile the player is still moving is not: while a turn is being
 * built the two have to stay tellable apart, so a tile with `placed` falsy is joined only to other
 * tiles with `placed` falsy, and the board it is being laid against keeps its own shape. Whether the
 * player's own tiles merge with each other is `mergeOwnTiles`.
 *
 * Two tiles sitting corner to corner are linked whichever way either was laid, because the move
 * rules spell a word down that corner either way. With `diagonals` off that corner spells nothing,
 * so the two tiles stay apart: no joint is drawn between them and each keeps its own shape. A
 * cluster tile's own cells still step corner to corner, being one tile.
 *
 * A cluster tile lying on a diagonal is a cluster in its own right, alone: its letters step corner to
 * corner exactly as two separate tiles would, so the rectangle `BoardTileFace` draws for `h` and `v`
 * cannot hold it. Grouped here, it gets the same jointed staircase the shape already draws for a
 * corner-touching pair.
 */
export function tileClusters(tiles: TileData[], options: ClusterOptions = {}): TileCluster[] {
  const { diagonals = DIAGONAL_WORDS, mergeOwnTiles = MERGE_OWN_TILES } = options;
  const byCell = new Map<string, { row: number; col: number; tile: TileData }>();
  for (const tile of tiles) {
    for (const c of computeCellsCovered(tile)) byCell.set(cellKey(c.row, c.col), { ...c, tile });
  }

  // Union-find over tile ids.
  const parent = new Map<string, string>(tiles.map((t) => [t.id, t.id]));
  const find = (id: string): string => {
    let root = id;
    while (parent.get(root) !== root) root = parent.get(root) as string;
    parent.set(id, root);
    return root;
  };

  const links: ClusterLink[] = [];

  for (const cell of byCell.values()) {
    for (const step of LINK_STEPS) {
      const other = byCell.get(cellKey(cell.row + step.dr, cell.col + step.dc));
      if (!other) continue;
      if (!diagonals && isDiagonalStep(step) && other.tile.id !== cell.tile.id) continue;
      if (!mergeable(cell.tile, other.tile, mergeOwnTiles)) continue;
      links.push({ from: { row: cell.row, col: cell.col }, to: { row: other.row, col: other.col } });
      parent.set(find(cell.tile.id), find(other.tile.id));
    }
  }

  const groups = new Map<string, TileData[]>();
  for (const tile of tiles) {
    const root = find(tile.id);
    groups.set(root, [...(groups.get(root) ?? []), tile]);
  }

  const clusters: TileCluster[] = [];
  for (const [root, members] of groups) {
    if (members.length < 2 && !members.some(isStaircase)) continue;
    const cells = [...byCell.values()]
      .filter((c) => find(c.tile.id) === root)
      .map((c) => ({ row: c.row, col: c.col, tileId: c.tile.id }));
    clusters.push({
      tiles: members,
      cells,
      links: links.filter((l) => find(byCell.get(cellKey(l.from.row, l.from.col))?.tile.id ?? '') === root),
    });
  }
  return clusters;
}

/** Width of the cluster outline — matches the 1.5px tile border. */
const OUTLINE_WIDTH = 1.5;

export type TileClusterGeometry = {
  /** Board-relative position and size of the cluster's SVG box. */
  left: number;
  top: number;
  width: number;
  height: number;
  /** Silhouette, filled in ink — only the rim left uncovered by `edge` shows, as the outline. */
  outline: string;
  /** The silhouette inset by the outline width — the tile-edge colour. */
  edge: string;
  /** The face, inset as far as a tile's inner panel. */
  face: string;
};

/** Corner radius of a board cell (`board-cell.module.scss`) — the curve a diagonal joint bends around. */
const CELL_RADIUS = 4;

/**
 * Radius of the arc the silhouette bends around a blank cell's corner at a gap crossing: the smallest
 * arc, tangent to the edges of the cells either side of the blank, that still clears the blank's
 * rounded corner. Its centre sits (radius - gap/2) past the crossing on each axis, and the blank's
 * corner arc must fit inside it.
 */
const gapArcRadius = (gap: number): number => (Math.SQRT2 * (gap + CELL_RADIUS) + CELL_RADIUS) / (1 + Math.SQRT2);

/**
 * Fillet for an inward corner of the silhouette, at the corner of `cell` that faces `e` — a blank
 * cell diagonally across a gap crossing, with both cells between them occupied. It fills the square
 * corner with the same arc a diagonal joint bends around a blank, so every inward turn curves alike.
 */
function inwardCornerPath(
  cellCentre: Point,
  e: { x: 1 | -1; y: 1 | -1 },
  cellSize: number,
  gap: number,
  inset: number,
): string {
  const r = gapArcRadius(gap);
  const arcRadius = r + inset;
  // The corner, pulled `inset` px back into the cell.
  const q = { x: cellCentre.x + e.x * (cellSize / 2 - inset), y: cellCentre.y + e.y * (cellSize / 2 - inset) };
  const alongX = { x: q.x + e.x * arcRadius, y: q.y };
  const alongY = { x: q.x, y: q.y + e.y * arcRadius };
  const [first, second] = e.x === e.y ? [alongX, alongY] : [alongY, alongX];
  const at = (p: Point) => `${round2(p.x)} ${round2(p.y)}`;
  return `M ${at(q)} L ${at(first)} A ${round2(arcRadius)} ${round2(arcRadius)} 0 0 0 ${at(second)} Z`;
}

/** Quadrants around a gap crossing, clockwise from top-left. */
const QUADRANTS: { sx: 1 | -1; sy: 1 | -1 }[] = [
  { sx: -1, sy: -1 },
  { sx: 1, sy: -1 },
  { sx: 1, sy: 1 },
  { sx: -1, sy: 1 },
];

/**
 * The joint of a diagonal, where two cells meet corner to corner across the crossing of the
 * gaps between them. Each blank neighbour is skirted by a concave arc tangent to the edges of both
 * joined cells, so the outline runs straight off one cell, around the blank's corner and onto the
 * other without a step. Returns nothing when the blanks on both sides pinch the joint shut.
 *
 * `occupied` lists the four quadrants around the crossing, clockwise from top-left.
 */
function stepJointPath(crossing: Point, occupied: boolean[], gap: number, inset: number): string | null {
  const baseRadius = gapArcRadius(gap);
  const centreOffset = baseRadius - gap / 2;
  // An inset arc shares its centre — it is the same curve pulled `inset` px further from the blank.
  const arcRadius = baseRadius + inset;
  const cellEdge = gap / 2 + inset;
  const reach = baseRadius + gap;

  const pinched = 2 * Math.SQRT2 * centreOffset <= 2 * arcRadius;
  if (pinched && ((!occupied[0] && !occupied[2]) || (!occupied[1] && !occupied[3]))) return null;

  const at = (x: number, y: number) => `${round2(crossing.x + x)} ${round2(crossing.y + y)}`;
  const parts: string[] = [];
  QUADRANTS.forEach(({ sx, sy }, i) => {
    const move = parts.length ? 'L' : 'M';
    if (occupied[i]) {
      parts.push(`${move} ${at(sx * reach, sy * reach)}`);
      return;
    }
    // Tangent points on the horizontal edge of the cell beside the blank's column, and on the vertical
    // edge of the cell beside its row. Clockwise, top-left and bottom-right are reached from the
    // former, the other two from the latter.
    const onHorizontal = at(sx * centreOffset, -sy * cellEdge);
    const onVertical = at(-sx * cellEdge, sy * centreOffset);
    const [first, second] = sx === sy ? [onHorizontal, onVertical] : [onVertical, onHorizontal];
    parts.push(`${move} ${first}`, `A ${round2(arcRadius)} ${round2(arcRadius)} 0 0 0 ${second}`);
  });
  parts.push('Z');
  return parts.join(' ');
}

/**
 * The cluster's silhouette, pulled `inset` px in from its true edge, as one multi-shape path:
 *
 * - each cell — a rounded square;
 * - a strip one cell thick from centre to centre of each side-by-side pair, bridging the gap between
 *   them, or a diagonal pair's joint at the corner they share;
 * - a square over the gap crossing inside every fully occupied 2×2 block, which the strips miss;
 * - a fillet in every inward corner, where three cells around a gap crossing wrap a blank.
 *
 * Under the nonzero fill rule the shapes merge: seams between cells vanish and only the outer edge
 * remains. A strip is inset across its thickness only — along its length it ends at cell centres,
 * well inside the silhouette.
 */
function clusterPath(
  cluster: TileCluster,
  cellSize: number,
  gap: number,
  origin: Point,
  inset: number,
  radius: number,
): string {
  const step = cellSize + gap;
  const centre = (row: number, col: number): Point => ({
    x: origin.x + col * step + cellSize / 2,
    y: origin.y + row * step + cellSize / 2,
  });
  const occupied = new Set(cluster.cells.map((c) => cellKey(c.row, c.col)));
  const parts: string[] = [];

  // Cell corners a diagonal joint attaches to, by cell — 0–3 clockwise from top-left. They stay square
  // so the joint meets the cell without a notch.
  const jointCorners = new Map<string, Set<number>>();
  const markCorner = (row: number, col: number, corner: number) => {
    const key = cellKey(row, col);
    jointCorners.set(key, (jointCorners.get(key) ?? new Set()).add(corner));
  };

  for (const link of cluster.links) {
    const diagonal = link.from.row !== link.to.row && link.from.col !== link.to.col;
    const a = centre(link.from.row, link.from.col);
    const b = centre(link.to.row, link.to.col);

    if (diagonal) {
      // Links step forward one column, so the shared corner is on `from`'s right: bottom-right going
      // down, top-right going up.
      const down = link.to.row > link.from.row;
      const top = Math.min(link.from.row, link.to.row);
      const left = link.from.col;
      const quadrants = [
        occupied.has(cellKey(top, left)),
        occupied.has(cellKey(top, left + 1)),
        occupied.has(cellKey(top + 1, left + 1)),
        occupied.has(cellKey(top + 1, left)),
      ];

      // The pair meets at the corner alone only while both cells flanking it are blank. Occupy
      // either and the two already join through it, and the strips and inward fillets draw that
      // crossing — a joint on top of them would square off corners that should stay curved.
      if (quadrants[down ? 1 : 0] || quadrants[down ? 3 : 2]) continue;

      markCorner(link.from.row, link.from.col, down ? 2 : 1);
      markCorner(link.to.row, link.to.col, down ? 0 : 3);

      const joint = stepJointPath({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, quadrants, gap, inset);
      if (joint) parts.push(joint);
      continue;
    }

    const u = unitTowards(a, b);
    const across = cellSize / 2 - inset;
    const p = { x: -u.y * across, y: u.x * across };
    parts.push(
      polygonPath(
        clockwise([
          { x: a.x + p.x, y: a.y + p.y },
          { x: b.x + p.x, y: b.y + p.y },
          { x: b.x - p.x, y: b.y - p.y },
          { x: a.x - p.x, y: a.y - p.y },
        ]),
      ),
    );
  }

  for (const cell of cluster.cells) {
    const c = centre(cell.row, cell.col);
    const half = cellSize / 2;
    const corners = [
      { x: c.x - half, y: c.y - half },
      { x: c.x + half, y: c.y - half },
      { x: c.x + half, y: c.y + half },
      { x: c.x - half, y: c.y + half },
    ];
    const square = jointCorners.get(cellKey(cell.row, cell.col));
    const radii = square ? corners.map((_, i) => (square.has(i) ? 0 : radius)) : radius;
    parts.push(roundedPolygonPath(insetPolygon(corners, inset), radii));
  }

  for (const cell of cluster.cells) {
    const { row, col } = cell;
    if (!occupied.has(cellKey(row, col + 1)) || !occupied.has(cellKey(row + 1, col))) continue;
    if (!occupied.has(cellKey(row + 1, col + 1))) continue;
    const c = { x: centre(row, col).x + step / 2, y: centre(row, col).y + step / 2 };
    const half = gap / 2 + inset;
    parts.push(
      polygonPath([
        { x: c.x - half, y: c.y - half },
        { x: c.x + half, y: c.y - half },
        { x: c.x + half, y: c.y + half },
        { x: c.x - half, y: c.y + half },
      ]),
    );
  }

  for (const cell of cluster.cells) {
    const { row, col } = cell;
    for (const { sx, sy } of QUADRANTS) {
      if (!occupied.has(cellKey(row, col + sx)) || !occupied.has(cellKey(row + sy, col))) continue;
      if (occupied.has(cellKey(row + sy, col + sx))) continue;
      parts.push(inwardCornerPath(centre(row, col), { x: sx, y: sy }, cellSize, gap, inset));
    }
  }

  return parts.join(' ');
}

/**
 * One continuous shape around a cluster, layered as a tile is: an ink outline, the tile-edge rim and
 * the face. Every corner the cells present to the outside is rounded to the tile radius; corners
 * where the cluster turns inward are filleted around the blank cell they wrap.
 */
export function tileClusterGeometry(
  cluster: TileCluster,
  cellSize: number,
  gap: number,
  padding: number,
): TileClusterGeometry {
  const step = cellSize + gap;
  const rows = cluster.cells.map((c) => c.row);
  const cols = cluster.cells.map((c) => c.col);
  const minRow = Math.min(...rows);
  const minCol = Math.min(...cols);

  const left = padding + minCol * step;
  const top = padding + minRow * step;
  const width = (Math.max(...cols) - minCol + 1) * step - gap;
  const height = (Math.max(...rows) - minRow + 1) * step - gap;

  // Cluster-box coordinates of cell (0, 0).
  const origin = { x: -minCol * step, y: -minRow * step };
  const radius = Math.max(5, cellSize * 0.2);
  const innerInset = OUTLINE_WIDTH + Math.max(2, cellSize * 0.08);
  const innerRadius = Math.max(3, cellSize * 0.14);

  return {
    left,
    top,
    width,
    height,
    outline: clusterPath(cluster, cellSize, gap, origin, 0, radius),
    edge: clusterPath(cluster, cellSize, gap, origin, OUTLINE_WIDTH, radius - OUTLINE_WIDTH),
    face: clusterPath(cluster, cellSize, gap, origin, innerInset, innerRadius),
  };
}

/**
 * The directions a tile may be turned through, in cycle order. Diagonals are dropped when the rule is
 * off, so a rotation can never land a tile on an orientation `validateTile` is bound to reject.
 */
export const rotationOrder = (diagonals = DIAGONAL_WORDS): Direction[] =>
  diagonals ? ['h', 'v', 'dr', 'ur'] : ['h', 'v'];

/**
 * A direction `steps` places round the cycle, backwards for a negative count. A tile already on a
 * direction the cycle no longer offers — a diagonal, after the rule was switched off — comes back to
 * the start rather than staying put.
 */
export function rotateDirection(dir: Direction, steps: number, diagonals = DIAGONAL_WORDS): Direction {
  const order = rotationOrder(diagonals);
  const index = order.indexOf(dir);
  if (index === -1) return order[0];
  return order[(((index + steps) % order.length) + order.length) % order.length];
}

/** The next direction round the cycle. */
export const directionCycle = (dir: Direction, diagonals = DIAGONAL_WORDS): Direction =>
  rotateDirection(dir, 1, diagonals);

/** How a direction reads aloud, for the board's live region. */
export const DIRECTION_LABEL: Record<Direction, string> = {
  h: 'across',
  v: 'down',
  dr: 'down and right',
  ur: 'up and right',
};
